import entries from 'lodash/entries'
import has from 'lodash/has'
import isNil from 'lodash/isNil'
import { css } from 'styled-components'

import { LONG_HEX_REG_EXP, SHORT_HEX_REG_EXP } from '@/constants/global'

import {
  BREAKPOINT_SIZE,
  FONT_SIZE,
  MARGINS,
  PADDINGS,
  SPACE,
} from './constants'

import type {
  Breakpoint,
  InnerType,
  Margin,
  MarginResponsive,
  Padding,
  PaddingResponsive,
  ResponsiveValue,
} from './types'
import type { DefaultTheme, Interpolation } from 'styled-components'

export const mobile = (inner: InnerType) => css`
  @media screen and (max-width: ${BREAKPOINT_SIZE.mobileMax}) {
    ${inner};
  }
`

export const tablet = (inner: InnerType) => css`
  @media screen and (min-width: ${BREAKPOINT_SIZE.tabletMin}) and (max-width: ${BREAKPOINT_SIZE.tabletMax}) {
    ${inner};
  }
`

export const desktop = (inner: InnerType) => css`
  @media screen and (min-width: ${BREAKPOINT_SIZE.desktopMin}) {
    ${inner};
  }
`

export const desktopSmall = (inner: InnerType) => css`
  @media screen and (min-width: ${BREAKPOINT_SIZE.desktopMin}) and (max-width: ${BREAKPOINT_SIZE.desktopSmall}) {
    ${inner};
  }
`

export const desktopMedium = (inner: InnerType) => {
  const minWidth = Number.parseFloat(BREAKPOINT_SIZE.desktopSmall) + 1
  return css`
    @media screen and (min-width: ${minWidth}px) and (max-width: ${BREAKPOINT_SIZE.desktopMedium}) {
      ${inner};
    }
  `
}

export const desktopLarge = (inner: InnerType) => {
  const minWidth = Number.parseFloat(BREAKPOINT_SIZE.desktopMedium) + 1

  return css`
    @media screen and (min-width: ${minWidth}px) and (max-width: ${BREAKPOINT_SIZE.desktopLarge}) {
      ${inner};
    }
  `
}

export const desktopMax = (inner: InnerType) => {
  const minWidth = Number.parseFloat(BREAKPOINT_SIZE.desktopLarge) + 1
  return css`
    @media screen and (min-width: ${minWidth}px) {
      ${inner};
    }
  `
}

export const mobileAndTablet = (inner: InnerType) => css`
  @media screen and (max-width: ${BREAKPOINT_SIZE.tabletMax}) {
    ${inner};
  }
`

export const tabletAndDesktop = (inner: InnerType) => css`
  @media screen and (min-width: ${BREAKPOINT_SIZE.tabletMin}) {
    ${inner};
  }
`

export const hasHover = (inner: InnerType) => css`
  @media (hover: hover) {
    ${inner};
  }
`

export const noHover = (inner: InnerType) => css`
  @media (hover: none) {
    ${inner};
  }
`

export const makeCustomScrollbar = (theme: DefaultTheme) => css`
  ::-webkit-scrollbar {
    width: 2px;
    height: 0;
    border-radius: 50px;
  }

  ::-webkit-scrollbar-track {
    background: ${convertHexInRGBA(theme.colors.slateGray, 0.1)};
    border-radius: 50px;
  }

  ::-webkit-scrollbar-thumb {
    background: ${theme.colors.coastalFjord};
    border-radius: 50px;
  }
`

export const makeVisuallyHidden = () => css`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`

// Multiline Ellipsis
export const makeLinesClamped = (lineCount: number) => css`
  display: -webkit-box;
  -webkit-line-clamp: ${lineCount};
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap; /* let the text wrap preserving spaces */
`

export const convertHexInRGBA = (hexColor: string, alpha = 1) => {
  const color = hexToRgb(hexColor)

  if (isNil(color)) {
    return null
  }

  return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`
}

export const hexToRgb = (rowHex: string) => {
  if (typeof rowHex !== 'string') {
    return null
  }

  const hex = rowHex.replace(
    SHORT_HEX_REG_EXP,
    (m, r, g, b) => r + r + g + g + b + b
  )
  const result = LONG_HEX_REG_EXP.exec(hex)

  if (isNil(result)) {
    return null
  }

  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  }
}

const BREAKPOINT_MAP: Record<
  Breakpoint,
  (inner: Interpolation<DefaultTheme>) => Interpolation<DefaultTheme>
> = {
  base: (inner: Interpolation<DefaultTheme>) => css`
    ${inner}
  `,
  m: mobile,
  mt: mobileAndTablet,
  t: tablet,
  td: tabletAndDesktop,
  d: desktop,
  ds: desktopSmall,
  dm: desktopMedium,
  dl: desktopLarge,
  dx: desktopMax,
}

export const createResponsiveStyles = <T>(
  value: ResponsiveValue<T>,
  inner: (value: T) => Interpolation<DefaultTheme>
): Interpolation<DefaultTheme> => {
  if (typeof value === 'string') {
    return inner(value)
  }

  if (typeof value === 'object' && value !== null) {
    return entries(value).reduce<Interpolation<DefaultTheme>>(
      (acc, [key, value]) => {
        const breakpoint = key as Breakpoint
        if (!(breakpoint in BREAKPOINT_MAP)) {
          return acc
        }
        return css`
          ${acc}
          ${BREAKPOINT_MAP[breakpoint](inner(value))}
        `
      },
      css``
    )
  }

  return css``
}

export const getFontSizeStyles = <T extends keyof typeof FONT_SIZE | 'inherit'>(
  size: ResponsiveValue<T>
): Interpolation<DefaultTheme> =>
  createResponsiveStyles(size, (value) =>
    has(FONT_SIZE, value)
      ? FONT_SIZE[value as keyof typeof FONT_SIZE]
      : css`
          font-size: ${value};
          line-height: ${value};
        `
  )

export const getMarginStyles = <
  K extends keyof Margin,
  V extends MarginResponsive[K],
>(
  margins: Partial<Record<K, V>>
): Interpolation<DefaultTheme> =>
  entries(margins).reduce<Interpolation<DefaultTheme>>(
    (acc, [key, value]) => {
      const mKey = key as K
      const mValue = value as V
      if (!(mKey in MARGINS)) {
        return acc
      }
      return css`
        ${acc}
        ${createResponsiveStyles(mValue, (value) =>
          MARGINS[mKey](has(SPACE, value) ? SPACE[value] : value)
        )}
      `
    },
    css``
  )

export const getPaddingStyles = <
  K extends keyof Padding,
  V extends PaddingResponsive[K],
>(
  paddings: Partial<Record<K, V>>
): Interpolation<DefaultTheme> =>
  entries(paddings).reduce<Interpolation<DefaultTheme>>(
    (acc, [key, value]) => {
      const pKey = key as K
      const pValue = value as V
      if (!(pKey in PADDINGS)) {
        return acc
      }
      return css`
        ${acc}
        ${createResponsiveStyles(pValue, (value) =>
          PADDINGS[pKey](has(SPACE, value) ? SPACE[value] : value)
        )}
      `
    },
    css``
  )

/**
 * @argument hex6 - 6 digit hex color
 * @argument opacity - opacity value between 0 and 1
 * @returns 8 digit hex color
 * @example getColorWithOpacity('#ff0000', 0.5) // '#ff000080'
 * @description Adds opacity to the hex color
 */
export const getColorWithOpacity = (hex6: string, opacity?: number) => {
  if (!hex6) {
    return
  }

  if (hex6.length !== 7 || isNil(opacity)) {
    return hex6
  }

  return `${hex6}${Math.round(opacity * 255).toString(16)}`
}
